import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Tabs,
  Table,
  Tag,
  Button,
  Space,
  Divider,
  Avatar,
  Descriptions,
  Switch,
  message,
  Spin,
  Modal,
  Input,
  Select,
  Image,
  Popconfirm,
  Breadcrumb, // Add this import
} from "antd";
import {
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  PlusOutlined,
  SearchOutlined,
  ExclamationCircleOutlined,
  ArrowLeftOutlined,
  HomeOutlined,
  ShoppingCartOutlined, // Add this import
} from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import "./UserDetails.css"; // Import your CSS file for custom styles
import UserOrders from "./UserOrders";

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Search } = Input;

const UserDetails = () => {
  const { userId } = useParams();
  const navigate = useNavigate(); // Add this hook
  const [loading, setLoading] = useState(true);
  const [userDetails, setUserDetails] = useState(null);
  const [downloadHistory, setDownloadHistory] = useState([]);
  const [activityHistory, setActivityHistory] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  // Modal states
  const [addProductModalVisible, setAddProductModalVisible] = useState(false);
  const [searchProducts, setSearchProducts] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedProducts, setSelectedProducts] = useState([]);

  // New state variables for activity summary
  const [activitySummary, setActivitySummary] = useState({
    screenSummary: [],
    actionSummary: [],
    totalActivities: 0,
  });

  // Add these new state variables after existing states
  const [actionFilterModal, setActionFilterModal] = useState(false);
  const [filteredActivities, setFilteredActivities] = useState([]);
  const [selectedAction, setSelectedAction] = useState("");

  useEffect(() => {
    fetchUserDetails();
    fetchDownloadHistory();
    fetchActivityHistory();
  }, [userId]);

  const fetchUserDetails = async () => {
    const tokenString = localStorage.getItem("token");
    try {
      console.log("Fetching user details for userId:", userId);
      let config = {
        method: "get",
        url:
          global.config.APIURL.BaseURL +
          `api/UserAccount/GetUserAccountDetailByUserID?UserID=${userId}`,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + tokenString,
        },
      };

      const response = await axios.request(config);
      setUserDetails(response.data);
    } catch (error) {
      console.error("Error fetching user details:", error);
      messageApi.error("Failed to fetch user details");
    } finally {
      setLoading(false);
    }
  };

  const fetchDownloadHistory = async () => {
    const tokenString = localStorage.getItem("token");
    try {
      let config = {
        method: "get",
        url:
          global.config.APIURL.BaseURL +
          `api/UserWiseProdDownload/GetAllowedProductstoDownload?UserID=${userId}`,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + tokenString,
        },
      };

      const response = await axios.request(config);
      setDownloadHistory(response.data);
    } catch (error) {
      console.error("Error fetching download history:", error);
    }
  };

  const calculateActivitySummary = (activities) => {
    const screenCount = {};
    const actionCount = {};

    activities.forEach((activity) => {
      // Count screens
      if (activity.ScreenName) {
        screenCount[activity.ScreenName] =
          (screenCount[activity.ScreenName] || 0) + 1;
      }

      // Count actions
      if (activity.DetailName) {
        actionCount[activity.DetailName] =
          (actionCount[activity.DetailName] || 0) + 1;
      }
    });

    // Convert to array and sort by count
    const screenSummary = Object.entries(screenCount)
      .map(([screen, count]) => ({ screen, count }))
      .sort((a, b) => b.count - a.count);

    const actionSummary = Object.entries(actionCount)
      .map(([action, count]) => ({ action, count }))
      .sort((a, b) => b.count - a.count);

    return {
      screenSummary,
      actionSummary,
      totalActivities: activities.length,
    };
  };

  const fetchActivityHistory = async () => {
    setActivityLoading(true);
    const tokenString = localStorage.getItem("token");
    try {
      let config = {
        method: "get",
        url:
          global.config.APIURL.BaseURL +
          `api/UserActivity/GetActivitybyUserID?UserID=${userId}`,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + tokenString,
        },
      };

      const response = await axios.request(config);
      setActivityHistory(response.data);

      // Calculate and set activity summary
      const summary = calculateActivitySummary(response.data);
      setActivitySummary(summary);
    } catch (error) {
      console.error("Error fetching activity history:", error);
      messageApi.error("Failed to fetch activity history");
    } finally {
      setActivityLoading(false);
    }
  };

  const searchProductsForDownload = async (searchValue) => {
    if (!searchValue || searchValue.length < 2) {
      setSearchProducts([]);
      return;
    }

    setSearchLoading(true);
    const tokenString = localStorage.getItem("token");
    try {
      let config = {
        method: "get",
        url:
          global.config.APIURL.BaseURL +
          `api/Product/SearchProductByText?SearchText=${searchValue}`,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + tokenString,
        },
      };

      const response = await axios.request(config);
      setSearchProducts(response.data);
    } catch (error) {
      console.error("Error searching products:", error);
      messageApi.error("Failed to search products");
    } finally {
      setSearchLoading(false);
    }
  };

  const handleAddProductsToDownload = async () => {
    if (selectedProducts.length === 0) {
      messageApi.warning("Please select at least one product");
      return;
    }

    const tokenString = localStorage.getItem("token");

    try {
      // Show loading message
      messageApi.loading("Adding products to download list...", 0);

      // Call API for each selected product
      const promises = selectedProducts.map((productId) => {
        let config = {
          method: "post",
          url:
            global.config.APIURL.BaseURL +
            `api/UserWiseProdDownload/AddProductToAllowedDownload?UserID=${userId}&ProductID=${productId}`,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + tokenString,
          },
        };

        return axios.request(config);
      });

      // Wait for all API calls to complete
      await Promise.all(promises);

      // Hide loading message
      messageApi.destroy();

      messageApi.success(
        `${selectedProducts.length} products added to download list successfully`
      );
      setAddProductModalVisible(false);
      setSelectedProducts([]);
      setSearchText("");
      setSearchProducts([]);
      fetchDownloadHistory(); // Refresh download history
    } catch (error) {
      // Hide loading message
      messageApi.destroy();

      console.error("Error adding products to download:", error);
      messageApi.error("Failed to add products to download");
    }
  };

  // Update the handleStatusChange function to use Popconfirm
  const handleStatusChange = async (field, value) => {
    const tokenString = localStorage.getItem("token");

    try {
      const hideLoading = message.loading(`Updating ${field}...`, 0);

      let config = {};

      // Different API endpoints based on field
      switch (field) {
        case "IsActive":
          config = {
            method: "post",
            url:
              global.config.APIURL.BaseURL + "api/UserAccount/SetActiveStatus",
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + tokenString,
            },
            data: {
              UserID: userId,
              ApproveVal: value,
            },
          };
          break;

        case "IsApproved":
          config = {
            method: "post",
            url:
              global.config.APIURL.BaseURL +
              "api/UserAccount/SetApprovalStatus",
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + tokenString,
            },
            data: {
              UserID: userId,
              ApproveVal: value,
            },
          };
          break;

        case "AllowtoOrder":
          config = {
            method: "post",
            url:
              global.config.APIURL.BaseURL +
              "api/UserAccount/SetAllowtoOrderStatus",
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + tokenString,
            },
            data: {
              UserID: userId,
              ApproveVal: value,
            },
          };
          break;

        case "ShowProductStatus":
          config = {
            method: "post",
            url:
              global.config.APIURL.BaseURL + "api/User/UpdateProductStatusView",
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + tokenString,
            },
            data: {
              UserID: userId,
              ApproveVal: value,
            },
          };
          break;

        default:
          hideLoading();
          throw new Error(`Unknown field: ${field}`);
      }

      await axios.request(config);

      // Hide loading message
      hideLoading();

      // Show success message
      const fieldName =
        field === "IsActive"
          ? "Active Status"
          : field === "IsApproved"
          ? "Approval Status"
          : field === "AllowtoOrder"
          ? "Allow to Order"
          : "Show Product Status";

      messageApi.success(`${fieldName} updated successfully`);

      // Update local state
      setUserDetails((prev) => ({
        ...prev,
        [field]: value,
      }));
    } catch (error) {
      console.error(`Error updating ${field}:`, error);

      const fieldName =
        field === "IsActive"
          ? "Active Status"
          : field === "IsApproved"
          ? "Approval Status"
          : field === "AllowtoOrder"
          ? "Allow to Order"
          : "Show Product Status";

      messageApi.error(`Failed to update ${fieldName}`);
    }
  };

  const handleRemoveFromDownload = async (productId) => {
    const tokenString = localStorage.getItem("token");
    try {
      let config = {
        method: "post",
        url:
          global.config.APIURL.BaseURL +
          `api/UserWiseProdDownload/RemoveProductforUserfromDownload?UserID=${userId}&ProductID=${productId}`,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + tokenString,
        },
      };

      await axios.request(config);
      messageApi.success("Product removed from download list successfully");
      fetchDownloadHistory(); // Refresh the list
    } catch (error) {
      console.error("Error removing product from download:", error);
      messageApi.error("Failed to remove product from download list");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";

    try {
      const date = new Date(dateString);

      if (isNaN(date.getTime())) {
        return "Invalid Date";
      }

      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();

      let hours = date.getHours();
      const minutes = date.getMinutes().toString().padStart(2, "0");
      const ampm = hours >= 12 ? "PM" : "AM";

      hours = hours % 12;
      hours = hours ? hours : 12;
      const formattedHours = hours.toString().padStart(2, "0");

      return `${day}-${month}-${year} ${formattedHours}:${minutes} ${ampm}`;
    } catch (error) {
      return "Invalid Date";
    }
  };

  const downloadColumns = [
    {
      title: "Image",
      dataIndex: "ImageUrl",
      key: "ImageUrl",
      render: (imageUrl) => (
        <Image
          src={imageUrl}
          alt="Product"
          width={50}
          height={50}
          style={{ objectFit: "cover", borderRadius: 4 }}
        />
      ),
      width: 70,
    },
    {
      title: "Product Name",
      dataIndex: "ProductName",
      key: "ProductName",
      width: 200,
    },
    {
      title: "Design No",
      dataIndex: "DesignNo",
      key: "DesignNo",
      width: 120,
    },
    {
      title: "Sales Rate",
      dataIndex: "SalesRate",
      key: "SalesRate",
      render: (price) => `₹${price?.toFixed(2)}`,
      width: 100,
    },
    {
      title: "Deal Rate",
      dataIndex: "DealRate",
      key: "DealRate",
      render: (price) => (price > 0 ? `₹${price?.toFixed(2)}` : "-"),
      width: 100,
    },
    {
      title: "MOQ",
      dataIndex: "MOQ",
      key: "MOQ",
      render: (moq) => `${moq} Box`,
      width: 80,
    },
    {
      title: "Stock Status",
      dataIndex: "IsInStock",
      key: "IsInStock",
      render: (isInStock) => (
        <Tag color={isInStock ? "green" : "red"}>
          {isInStock ? "In Stock" : "Out of Stock"}
        </Tag>
      ),
      width: 100,
    },
    {
      title: "Status",
      dataIndex: "IsActive",
      key: "IsActive",
      render: (isActive) => (
        <Tag color={isActive ? "green" : "red"}>
          {isActive ? "Active" : "Inactive"}
        </Tag>
      ),
      width: 80,
    },
    {
      title: "Deal",
      dataIndex: "IsInDeal",
      key: "IsInDeal",
      render: (isInDeal) => (
        <Tag color={isInDeal ? "orange" : "default"}>
          {isInDeal ? "Deal" : "Regular"}
        </Tag>
      ),
      width: 80,
    },
    {
      title: "Tags",
      key: "tags",
      render: (_, record) => (
        <Space size="small">
          {record.Tag1 && <Tag color="blue">{record.Tag1}</Tag>}
          {record.Tag2 && <Tag color="purple">{record.Tag2}</Tag>}
        </Space>
      ),
      width: 150,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Popconfirm
          title="Remove Product"
          description={`Are you sure you want to remove "${record.ProductName}" from the download list?`}
          onConfirm={() => handleRemoveFromDownload(record.ProductID)}
          okText="Yes, Remove"
          cancelText="Cancel"
          okType="danger"
          icon={<ExclamationCircleOutlined style={{ color: "red" }} />}
        >
          <Button type="link" danger size="small">
            Remove
          </Button>
        </Popconfirm>
      ),
      width: 80,
    },
  ];

  const productSearchColumns = [
    {
      title: "Select",
      key: "select",
      render: (_, record) => (
        <input
          type="checkbox"
          checked={selectedProducts.includes(record.ProductID)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedProducts([...selectedProducts, record.ProductID]);
            } else {
              setSelectedProducts(
                selectedProducts.filter((id) => id !== record.ProductID)
              );
            }
          }}
        />
      ),
      width: 60,
    },
    {
      title: "Image",
      dataIndex: "ImageUrl",
      key: "ImageUrl",
      render: (imageUrl) => (
        <Image
          src={imageUrl}
          alt="Product"
          width={50}
          height={50}
          style={{ objectFit: "cover", borderRadius: 4 }}
        />
      ),
      width: 70,
    },
    {
      title: "Product Name",
      dataIndex: "ProductName",
      key: "ProductName",
      width: 200,
    },
    {
      title: "Design No",
      dataIndex: "DesignNo",
      key: "DesignNo",
      width: 120,
    },
    {
      title: "Category",
      dataIndex: "Category",
      key: "Category",
      width: 120,
    },
    {
      title: "Sales Rate",
      dataIndex: "SalesRate",
      key: "SalesRate",
      render: (price) => `₹${price?.toFixed(2)}`,
      width: 100,
    },
    {
      title: "Deal Rate",
      dataIndex: "DealRate",
      key: "DealRate",
      render: (price) => (price > 0 ? `₹${price?.toFixed(2)}` : "-"),
      width: 100,
    },
    {
      title: "MOQ",
      dataIndex: "MOQ",
      key: "MOQ",
      render: (moq) => `${moq} Box`,
      width: 80,
    },
    {
      title: "Status",
      dataIndex: "IsActive",
      key: "IsActive",
      render: (isActive) => (
        <Tag color={isActive ? "green" : "red"}>
          {isActive ? "Active" : "Inactive"}
        </Tag>
      ),
      width: 80,
    },
  ];

  // Simplified activity history columns without grouping
  const activityColumns = [
    {
      title: "Date & Time",
      dataIndex: "DateTime",
      key: "DateTime",
      render: (dateTime) => (
        <Text style={{ fontSize: "12px" }}>{formatDate(dateTime)}</Text>
      ),
      width: "20%",
      sorter: (a, b) => new Date(b.DateTime) - new Date(a.DateTime),
      defaultSortOrder: "descend",
    },
    {
      title: "Screen",
      dataIndex: "ScreenName",
      key: "ScreenName",
      render: (screenName) => (
        <Tag color="blue" style={{ fontSize: "11px" }}>
          {screenName}
        </Tag>
      ),
      width: "15%",
      filters: [...new Set(activityHistory.map((item) => item.ScreenName))].map(
        (screen) => ({
          text: screen,
          value: screen,
        })
      ),
      onFilter: (value, record) => record.ScreenName === value,
    },
    {
      title: "Action",
      dataIndex: "DetailName",
      key: "DetailName",
      render: (detailName) => (
        <Tag color="green" style={{ fontSize: "11px" }}>
          {detailName}
        </Tag>
      ),
      width: "20%",
      filters: [...new Set(activityHistory.map((item) => item.DetailName))].map(
        (detail) => ({
          text: detail,
          value: detail,
        })
      ),
      onFilter: (value, record) => record.DetailName === value,
    },
    {
      title: "Details",
      dataIndex: "Detail",
      key: "Detail",
      render: (detail) => (
        <Text style={{ fontSize: "12px" }}>{detail || "N/A"}</Text>
      ),
      width: "30%",
      ellipsis: {
        showTitle: false,
      },
    },
  ];

  // New summary columns for screen summary
  const screenSummaryColumns = [
    {
      title: "Screen Name",
      dataIndex: "screen",
      key: "screen",
      render: (screen) => (
        <Tag color="blue" style={{ fontSize: "12px" }}>
          {screen}
        </Tag>
      ),
      width: "70%",
    },
    {
      title: "Visit Count",
      dataIndex: "count",
      key: "count",
      render: (count) => (
        <span
          style={{
            fontWeight: "bold",
            color: "#1890ff",
            fontSize: "14px",
          }}
        >
          {count}
        </span>
      ),
      width: "30%",
      align: "center",
      sorter: (a, b) => b.count - a.count,
    },
  ];

  // New summary columns for action summary
  const actionSummaryColumns = [
    {
      title: "Action Name",
      dataIndex: "action",
      key: "action",
      render: (action) => (
        <Tag
          color="green"
          style={{ fontSize: "12px", cursor: "pointer" }}
          onClick={() => handleActionClick(action)}
        >
          {action}
        </Tag>
      ),
      width: "70%",
    },
    {
      title: "Action Count",
      dataIndex: "count",
      key: "count",
      render: (count, record) => (
        <span
          style={{
            fontWeight: "bold",
            color: "#52c41a",
            fontSize: "14px",
            cursor: "pointer",
          }}
          onClick={() => handleActionClick(record.action)}
        >
          {count}
        </span>
      ),
      width: "30%",
      align: "center",
      sorter: (a, b) => b.count - a.count,
    },
  ];

  // Simplified table styles without group header styles
  const tableStyles = `
    .table-row-light {
      background-color: #ffffff;
    }
    
    .table-row-dark {
      background-color: #fafafa;
    }
    
    .table-row-light:hover td,
    .table-row-dark:hover td {
      background-color: #e6f7ff !important;
    }
    
    .ant-table-thead > tr > th {
      background-color: #f5f5f5 !important;
      font-weight: 600 !important;
    }
  `;

  const handleActionClick = (actionName) => {
    const filtered = activityHistory.filter(
      (activity) => activity.DetailName === actionName
    );
    setFilteredActivities(filtered);
    setSelectedAction(actionName);
    setActionFilterModal(true);
  };

  const filteredActivityColumns = [
    {
      title: "Date & Time",
      dataIndex: "DateTime",
      key: "DateTime",
      render: (dateTime) => (
        <Text style={{ fontSize: "12px" }}>{formatDate(dateTime)}</Text>
      ),
      width: "25%",
      sorter: (a, b) => new Date(b.DateTime) - new Date(a.DateTime),
      defaultSortOrder: "descend",
    },
    {
      title: "Screen",
      dataIndex: "ScreenName",
      key: "ScreenName",
      render: (screenName) => (
        <Tag color="blue" style={{ fontSize: "11px" }}>
          {screenName}
        </Tag>
      ),
      width: "20%",
    },
    {
      title: "Action",
      dataIndex: "DetailName",
      key: "DetailName",
      render: (detailName) => (
        <Tag color="green" style={{ fontSize: "11px" }}>
          {detailName}
        </Tag>
      ),
      width: "20%",
    },
    {
      title: "Details",
      dataIndex: "Detail",
      key: "Detail",
      render: (detail) => (
        <Text style={{ fontSize: "12px" }}>{detail || "N/A"}</Text>
      ),
      width: "35%",
      ellipsis: {
        showTitle: false,
      },
    },
  ];

  // Add this function for back navigation with confirmation
  const handleBackToList = () => {
    navigate("/userlist");
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!userDetails) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Text>User not found</Text>
      </div>
    );
  }

  return (
    <div style={{ padding: "24px" }}>
      {contextHolder}

      <style>{tableStyles}</style>

      {/* Breadcrumb Navigation */}
      <div style={{ marginBottom: "24px" }}>
        <Breadcrumb
          items={[
            {
              href: "/admin",
              title: (
                <span>
                  <HomeOutlined />
                  <span>Admin</span>
                </span>
              ),
            },
            {
              href: "/userlist",
              title: (
                <span>
                  <UserOutlined />
                  <span>User Management</span>
                </span>
              ),
            },
            {
              title: userDetails ? userDetails.FirmName : "User Details",
            },
          ]}
        />

        <div
          style={{
            marginTop: "16px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Title level={2} style={{ margin: 0 }}>
            User Details
          </Title>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={handleBackToList}
            type="primary"
            size="large"
          >
            Back to User List
          </Button>
        </div>
      </div>

      {/* User Details Header */}
      <Card style={{ marginBottom: "24px" }}>
        <Row gutter={8}>
          <Col span={2}>
            <Avatar size={80} icon={<UserOutlined />} />
          </Col>
          <Col span={30}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <div>
                <Title level={2} style={{ margin: 0 }}>
                  {userDetails.FirmName}
                </Title>
                <Text type="secondary" style={{ fontSize: "16px" }}>
                  {userDetails.OwnerName}
                </Text>
                <div style={{ marginTop: "12px" }}>
                  <Space size="large">
                    <Tag color={userDetails.IsActive ? "green" : "red"}>
                      {userDetails.IsActive ? "Active" : "Inactive"}
                    </Tag>
                    <Tag color={userDetails.IsApproved ? "blue" : "orange"}>
                      {userDetails.IsApproved ? "Approved" : "Pending"}
                    </Tag>
                    <Tag color={userDetails.AllowtoOrder ? "green" : "red"}>
                      {userDetails.AllowtoOrder ? "Can Order" : "Cannot Order"}
                    </Tag>
                  </Space>
                </div>
              </div>
            </div>
          </Col>
        </Row>

        <Divider />

        <Descriptions column={2} bordered>
          <Descriptions.Item label="Contact Number" span={1}>
            <PhoneOutlined style={{ marginRight: "8px" }} />
            {userDetails.MobileNo1}
          </Descriptions.Item>

          <Descriptions.Item label="City" span={1}>
            <EnvironmentOutlined style={{ marginRight: "8px" }} />
            {userDetails.City}
          </Descriptions.Item>
          <Descriptions.Item label="Email" span={1}>
            <MailOutlined style={{ marginRight: "8px" }} />
            {userDetails.Email1}
          </Descriptions.Item>
          <Descriptions.Item label="State" span={1}>
            {userDetails.State}
          </Descriptions.Item>
        </Descriptions>

        <Divider />

        {/* Status Controls - Enhanced Version */}
        <Row gutter={24}>
          <Col span={6}>
            <Card size="small" style={{ textAlign: "center" }}>
              <Space direction="vertical" size="small">
                <Text strong>Active Status</Text>
                <Popconfirm
                  title={
                    <div>
                      <strong>Confirm Status Change</strong>
                    </div>
                  }
                  description={
                    <div>
                      <p>
                        Are you sure you want to{" "}
                        <strong>
                          {!userDetails.IsActive ? "activate" : "deactivate"}
                        </strong>{" "}
                        this user?
                      </p>
                      <p style={{ color: "#666", fontSize: "12px" }}>
                        User: <strong>{userDetails.FirmName}</strong>
                      </p>
                    </div>
                  }
                  onConfirm={() =>
                    handleStatusChange("IsActive", !userDetails.IsActive)
                  }
                  okText="Yes, Update"
                  cancelText="Cancel"
                  okType="primary"
                  icon={
                    <ExclamationCircleOutlined style={{ color: "#faad14" }} />
                  }
                  placement="top"
                >
                  <Switch
                    checked={userDetails.IsActive}
                    checkedChildren="Active"
                    unCheckedChildren="Inactive"
                    style={{
                      backgroundColor: userDetails.IsActive
                        ? "#52c41a"
                        : "#ff4d4f",
                    }}
                  />
                </Popconfirm>
                <Text type="secondary" style={{ fontSize: "12px" }}>
                  {userDetails.IsActive ? "User is active" : "User is inactive"}
                </Text>
              </Space>
            </Card>
          </Col>

          <Col span={6}>
            <Card size="small" style={{ textAlign: "center" }}>
              <Space direction="vertical" size="small">
                <Text strong>Approval Status</Text>
                <Popconfirm
                  title={
                    <div>
                      <strong>Confirm Approval Change</strong>
                    </div>
                  }
                  description={
                    <div>
                      <p>
                        Are you sure you want to{" "}
                        <strong>
                          {!userDetails.IsApproved ? "approve" : "disapprove"}
                        </strong>{" "}
                        this user?
                      </p>
                      <p style={{ color: "#666", fontSize: "12px" }}>
                        User: <strong>{userDetails.FirmName}</strong>
                      </p>
                    </div>
                  }
                  onConfirm={() =>
                    handleStatusChange("IsApproved", !userDetails.IsApproved)
                  }
                  okText="Yes, Update"
                  cancelText="Cancel"
                  okType="primary"
                  icon={
                    <ExclamationCircleOutlined style={{ color: "#faad14" }} />
                  }
                  placement="top"
                >
                  <Switch
                    checked={userDetails.IsApproved}
                    checkedChildren="Approved"
                    unCheckedChildren="Pending"
                    style={{
                      backgroundColor: userDetails.IsApproved
                        ? "#1890ff"
                        : "#faad14",
                    }}
                  />
                </Popconfirm>
                <Text type="secondary" style={{ fontSize: "12px" }}>
                  {userDetails.IsApproved
                    ? "User is approved"
                    : "Approval pending"}
                </Text>
              </Space>
            </Card>
          </Col>

          <Col span={6}>
            <Card size="small" style={{ textAlign: "center" }}>
              <Space direction="vertical" size="small">
                <Text strong>Allow to Order</Text>
                <Popconfirm
                  title={
                    <div>
                      <strong>Confirm Order Permission</strong>
                    </div>
                  }
                  description={
                    <div>
                      <p>
                        Are you sure you want to{" "}
                        <strong>
                          {!userDetails.AllowtoOrder ? "allow" : "disallow"}
                        </strong>{" "}
                        this user to place orders?
                      </p>
                      <p style={{ color: "#666", fontSize: "12px" }}>
                        User: <strong>{userDetails.FirmName}</strong>
                      </p>
                    </div>
                  }
                  onConfirm={() =>
                    handleStatusChange(
                      "AllowtoOrder",
                      !userDetails.AllowtoOrder
                    )
                  }
                  okText="Yes, Update"
                  cancelText="Cancel"
                  okType="primary"
                  icon={
                    <ExclamationCircleOutlined style={{ color: "#faad14" }} />
                  }
                  placement="top"
                >
                  <Switch
                    checked={userDetails.AllowtoOrder}
                    checkedChildren="Allowed"
                    unCheckedChildren="Not Allowed"
                    style={{
                      backgroundColor: userDetails.AllowtoOrder
                        ? "#52c41a"
                        : "#ff4d4f",
                    }}
                  />
                </Popconfirm>
                <Text type="secondary" style={{ fontSize: "12px" }}>
                  {userDetails.AllowtoOrder
                    ? "Can place orders"
                    : "Cannot place orders"}
                </Text>
              </Space>
            </Card>
          </Col>

          <Col span={6}>
            <Card size="small" style={{ textAlign: "center" }}>
              <Space direction="vertical" size="small">
                <Text strong>Show Product Status</Text>
                <Popconfirm
                  title={
                    <div>
                      <strong>Confirm Product Status Visibility</strong>
                    </div>
                  }
                  description={
                    <div>
                      <p>
                        Are you sure you want to{" "}
                        <strong>
                          {!userDetails.ShowProductStatus ? "show" : "hide"}
                        </strong>{" "}
                        product status for this user?
                      </p>
                      <p style={{ color: "#666", fontSize: "12px" }}>
                        User: <strong>{userDetails.FirmName}</strong>
                      </p>
                    </div>
                  }
                  onConfirm={() =>
                    handleStatusChange(
                      "ShowProductStatus",
                      !userDetails.ShowProductStatus
                    )
                  }
                  okText="Yes, Update"
                  cancelText="Cancel"
                  okType="primary"
                  icon={
                    <ExclamationCircleOutlined style={{ color: "#faad14" }} />
                  }
                  placement="top"
                >
                  <Switch
                    checked={userDetails.ShowProductStatus}
                    checkedChildren="Shown"
                    unCheckedChildren="Hidden"
                    style={{
                      backgroundColor: userDetails.ShowProductStatus
                        ? "#722ed1"
                        : "#d9d9d9",
                    }}
                  />
                </Popconfirm>
                <Text type="secondary" style={{ fontSize: "12px" }}>
                  {userDetails.ShowProductStatus
                    ? "Can see product status"
                    : "Cannot see product status"}
                </Text>
              </Space>
            </Card>
          </Col>
        </Row>
      </Card>

      {/* Tabs Section */}
      <Card>
        <Tabs defaultActiveKey="downloads">
          <TabPane
            tab={
              <span>
                <DownloadOutlined />
                Allowed Downloads ({downloadHistory.length})
              </span>
            }
            key="downloads"
          >
            <div style={{ marginBottom: "16px" }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setAddProductModalVisible(true)}
              >
                Add Product To Download
              </Button>
            </div>
            <Table
              columns={downloadColumns}
              dataSource={downloadHistory}
              rowKey="ProductID"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`,
              }}
              scroll={{ x: 1000 }}
            />
          </TabPane>

          {/* Activity History Tab */}
          <TabPane
            tab={
              <span>
                <UserOutlined />
                Activity History ({activityHistory.length})
              </span>
            }
            key="activity"
          >
            <div style={{ marginBottom: "16px" }}>
              <Space>
                <Button
                  icon={<SearchOutlined />}
                  onClick={fetchActivityHistory}
                  loading={activityLoading}
                >
                  Refresh
                </Button>

                <Text type="secondary">
                  Total activities: {activityHistory.length}
                </Text>
              </Space>
            </div>

            <Table
              columns={activityColumns}
              dataSource={activityHistory}
              rowKey={(record, index) => `${record.DateTime}-${index}`}
              loading={activityLoading}
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`,
                pageSizeOptions: ["10", "20", "50", "100"],
              }}
              size="small"
              locale={{
                emptyText: "No activity history found",
              }}
              scroll={{ x: 800 }}
              rowClassName={(record, index) =>
                index % 2 === 0 ? "table-row-light" : "table-row-dark"
              }
            />
          </TabPane>

          {/* NEW: Activity Summary Tab */}
          <TabPane
            tab={
              <span>
                <CheckCircleOutlined />
                Activity Summary
              </span>
            }
            key="activity-summary"
          >
            <div style={{ marginBottom: "16px" }}>
              <Space>
                <Button
                  icon={<SearchOutlined />}
                  onClick={fetchActivityHistory}
                  loading={activityLoading}
                >
                  Refresh Summary
                </Button>

                <Text type="secondary">
                  Total activities analyzed: {activitySummary.totalActivities}
                </Text>
              </Space>
            </div>

            <Row gutter={24}>
              {/* Screen Summary */}
              <Col span={12}>
                <Card
                  title="Screen Visit Summary"
                  size="small"
                  style={{ marginBottom: "16px" }}
                >
                  <div style={{ marginBottom: "8px" }}>
                    <Text strong>Most visited screens:</Text>
                  </div>
                  <Table
                    columns={screenSummaryColumns}
                    dataSource={activitySummary.screenSummary}
                    rowKey="screen"
                    pagination={{
                      pageSize: 10,
                      size: "small",
                      showTotal: (total, range) =>
                        `${range[0]}-${range[1]} of ${total} screens`,
                    }}
                    size="small"
                    loading={activityLoading}
                    locale={{
                      emptyText: "No screen data available",
                    }}
                    rowClassName={(record, index) =>
                      index % 2 === 0 ? "table-row-light" : "table-row-dark"
                    }
                  />
                </Card>
              </Col>

              {/* Action Summary */}
              <Col span={12}>
                <Card
                  title="Action Summary"
                  size="small"
                  style={{ marginBottom: "16px" }}
                >
                  <div style={{ marginBottom: "8px" }}>
                    <Text strong>Most performed actions:</Text>
                    <Text
                      type="secondary"
                      style={{ marginLeft: "8px", fontSize: "11px" }}
                    >
                      (Click on any action to view detailed activities)
                    </Text>
                  </div>
                  <Table
                    columns={actionSummaryColumns}
                    dataSource={activitySummary.actionSummary}
                    rowKey="action"
                    pagination={{
                      pageSize: 10,
                      size: "small",
                      showTotal: (total, range) =>
                        `${range[0]}-${range[1]} of ${total} actions`,
                    }}
                    size="small"
                    loading={activityLoading}
                    locale={{
                      emptyText: "No action data available",
                    }}
                    rowClassName={(record, index) =>
                      index % 2 === 0 ? "table-row-light" : "table-row-dark"
                    }
                  />
                </Card>
              </Col>
            </Row>

            {/* Summary Statistics */}
            <Card title="Quick Stats" size="small">
              <Row gutter={16}>
                <Col span={6}>
                  <div style={{ textAlign: "center", padding: "16px" }}>
                    <div
                      style={{
                        fontSize: "24px",
                        fontWeight: "bold",
                        color: "#1890ff",
                      }}
                    >
                      {activitySummary.totalActivities}
                    </div>
                    <div style={{ color: "#666" }}>Total Activities</div>
                  </div>
                </Col>
                <Col span={6}>
                  <div style={{ textAlign: "center", padding: "16px" }}>
                    <div
                      style={{
                        fontSize: "24px",
                        fontWeight: "bold",
                        color: "#52c41a",
                      }}
                    >
                      {activitySummary.screenSummary.length}
                    </div>
                    <div style={{ color: "#666" }}>Unique Screens</div>
                  </div>
                </Col>
                <Col span={6}>
                  <div style={{ textAlign: "center", padding: "16px" }}>
                    <div
                      style={{
                        fontSize: "24px",
                        fontWeight: "bold",
                        color: "#fa8c16",
                      }}
                    >
                      {activitySummary.actionSummary.length}
                    </div>
                    <div style={{ color: "#666" }}>Unique Actions</div>
                  </div>
                </Col>
                <Col span={6}>
                  <div style={{ textAlign: "center", padding: "16px" }}>
                    <div
                      style={{
                        fontSize: "24px",
                        fontWeight: "bold",
                        color: "#722ed1",
                      }}
                    >
                      {activitySummary.screenSummary.length > 0
                        ? Math.round(
                            activitySummary.totalActivities /
                              activitySummary.screenSummary.length
                          )
                        : 0}
                    </div>
                    <div style={{ color: "#666" }}>Avg per Screen</div>
                  </div>
                </Col>
              </Row>
            </Card>
          </TabPane>

          <TabPane
            tab={
              <span>
                <ShoppingCartOutlined />
                Orders
              </span>
            }
            key="orders"
          >
            <UserOrders userId={userId} />
          </TabPane>
        </Tabs>
      </Card>

      {/* Add Product Modal */}
      <Modal
        title="Add Products to Download"
        open={addProductModalVisible}
        onCancel={() => {
          setAddProductModalVisible(false);
          setSelectedProducts([]);
          setSearchText("");
          setSearchProducts([]);
        }}
        onOk={handleAddProductsToDownload}
        okText="Add Selected Products"
        width={1000}
        okButtonProps={{ disabled: selectedProducts.length === 0 }}
      >
        <div style={{ marginBottom: "16px" }}>
          <Search
            placeholder="Search products by name, design number, or category..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={searchProductsForDownload}
            loading={searchLoading}
            enterButton={<SearchOutlined />}
            style={{ marginBottom: "16px" }}
          />
        </div>

        {searchProducts.length > 0 && (
          <div style={{ marginBottom: "16px" }}>
            <Text strong>
              Selected: {selectedProducts.length} products | Found:{" "}
              {searchProducts.length} products
            </Text>
          </div>
        )}

        <Table
          columns={productSearchColumns}
          dataSource={searchProducts}
          rowKey="ProductID"
          pagination={{
            pageSize: 8,
            size: "small",
            showSizeChanger: true,
            showQuickJumper: true,
          }}
          size="small"
          loading={searchLoading}
          locale={{
            emptyText: searchText
              ? "No products found"
              : "Enter search text to find products",
          }}
          scroll={{ x: 800 }}
        />
      </Modal>

      {/* NEW: Action Filter Modal */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Tag color="green" style={{ fontSize: "14px" }}>
              {selectedAction}
            </Tag>
            <span>Activities</span>
          </div>
        }
        open={actionFilterModal}
        onCancel={() => {
          setActionFilterModal(false);
          setFilteredActivities([]);
          setSelectedAction("");
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setActionFilterModal(false);
              setFilteredActivities([]);
              setSelectedAction("");
            }}
          >
            Close
          </Button>,
        ]}
        width={1200}
      >
        <div style={{ marginBottom: "16px" }}>
          <Space>
            <Text strong>Action:</Text>
            <Tag color="green" style={{ fontSize: "12px" }}>
              {selectedAction}
            </Tag>
            <Text type="secondary">
              Total occurrences: {filteredActivities.length}
            </Text>
          </Space>
        </div>

        <Table
          columns={filteredActivityColumns}
          dataSource={filteredActivities}
          rowKey={(record, index) => `${record.DateTime}-${index}`}
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} activities`,
            pageSizeOptions: ["10", "15", "25", "50"],
          }}
          size="small"
          locale={{
            emptyText: "No activities found for this action",
          }}
          scroll={{ x: 800 }}
          rowClassName={(record, index) =>
            index % 2 === 0 ? "table-row-light" : "table-row-dark"
          }
        />
      </Modal>
    </div>
  );
};

export default UserDetails;
